import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';

import { api } from '@/api/client';
import { createCredential, explain } from '@/auth/passkey';
import { getAdmin } from '@/auth/session';

interface Passkey {
  id: number;
  credential_id: string;
  name: string | null;
  created_at: number;
  last_used_at: number | null;
}

export function ProfilePage() {
  const me = getAdmin();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    void api<{ passkeys: Passkey[] }>('/ops/api/passkeys')
      /**
       * ⚠ `?? []` because the generic is a CLAIM, not a check. `api<{ passkeys:
       * Passkey[] }>` tells TypeScript what the body will be; it does not make
       * it so. A response missing the key sets state to `undefined`, and the
       * next `passkeys.length` throws during render — a white screen, from a
       * server that answered 200. All five list pages had this shape.
       */
      .then((r) => setPasskeys(r.passkeys ?? []))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  /**
   * Add a passkey for THIS device, from inside an authenticated session.
   *
   * ⚠ This is the recovery path, and it has to be used before it is needed.
   * There is no password reset: an admin with one passkey on one device is one
   * lost laptop away from being locked out permanently.
   */
  const addPasskey = useCallback(async () => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const begin = await api<{ options: { publicKey: Parameters<typeof createCredential>[0] } }>(
        '/ops/api/passkey/register/begin',
        { method: 'POST' }
      );
      const credential = await createCredential(begin.options.publicKey);
      const qs = newName ? `?name=${encodeURIComponent(newName)}` : '';
      await api(`/ops/api/passkey/register/finish${qs}`, {
        method: 'POST',
        body: JSON.stringify(credential),
      });
      setNewName('');
      setNote('Passkey added. It works on this device from now on.');
      load();
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(false);
    }
  }, [newName, load]);

  const remove = useCallback(
    async (credentialId: string) => {
      setError(null);
      try {
        await api(`/ops/api/passkeys/${encodeURIComponent(credentialId)}`, { method: 'DELETE' });
        load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load]
  );

  const saveProfile = useCallback(async () => {
    setError(null);
    try {
      await api('/ops/api/profile', {
        method: 'PUT',
        body: JSON.stringify({ displayName }),
      });
      setNote('Saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [displayName]);

  return (
    <Page title={me?.username ?? 'Profile'}>
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}
        {note && (
          <Banner tone="success" onDismiss={() => setNote(null)}>
            <p>{note}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="300">
            <TextField
              label="Display name"
              value={displayName}
              onChange={setDisplayName}
              autoComplete="name"
            />
            <InlineStack>
              <Button onClick={() => void saveProfile()}>Save</Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingSm">
              Passkeys
            </Text>
            {passkeys.length <= 1 && (
              <Banner tone="warning" title="Add a second device">
                <p>
                  There is no password and no reset. With one passkey on one device, losing that
                  device locks you out permanently — the only way back would be editing the database
                  by hand.
                </p>
              </Banner>
            )}
            <BlockStack gap="200">
              {passkeys.map((p) => (
                <InlineStack key={p.id} align="space-between" blockAlign="center">
                  <BlockStack gap="050">
                    <Text as="span" fontWeight="semibold">
                      {p.name || `Passkey ${p.id}`}
                    </Text>
                    <Text as="span" tone="subdued" variant="bodySm">
                      Added {new Date(p.created_at * 1000).toLocaleDateString()}
                      {p.last_used_at
                        ? ` · last used ${new Date(p.last_used_at * 1000).toLocaleDateString()}`
                        : ' · never used'}
                    </Text>
                  </BlockStack>
                  <Button
                    variant="plain"
                    tone="critical"
                    disabled={passkeys.length <= 1}
                    onClick={() => void remove(p.credential_id)}
                  >
                    Remove
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>

            <TextField
              label="Name this device"
              value={newName}
              onChange={setNewName}
              autoComplete="off"
              placeholder="MacBook, phone, YubiKey…"
            />
            <InlineStack>
              <Button variant="primary" loading={busy} onClick={() => void addPasskey()}>
                Add a passkey on this device
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
